// imports
import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2 } from '@withjoy/joykit';

// local imports
import { useTranslation } from '@shared/core';
import { StarRating } from '@shared/components';
import { DEFAULT_HOTEL_IMAGE_WIDE } from '@shared/utils/constants';
import { AddressMultiline } from '@shared/components/AddressMultiline';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { AccommodationCombined } from '../../../Accommodations/Accommodation.types';
import { AddressMultilineProps } from '@shared/components/AddressMultiline/AddressMultiline';

// interfaces
interface HotelInfoSectionProps {
  discountPercentage: number | null;
  accommodation: AccommodationCombined;
}

export const HotelInfoSection: React.FC<HotelInfoSectionProps> = React.memo(({ accommodation, discountPercentage }) => {
  const isMobile = useIsMobileScreen();

  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');

  const addressObject: AddressMultilineProps['address'] = useMemo(
    () => ({
      line1: accommodation.address,
      city: accommodation.city,
      state: accommodation.state,
      country: accommodation.country,
      postalCode: accommodation.postalCode,
    }),
    [accommodation.address, accommodation.city, accommodation.state, accommodation.country, accommodation.postalCode],
  );

  return (
    <Flex gap={pxToRem(24)}>
      <Flex
        flexShrink={0}
        height={'auto'}
        backgroundSize="cover"
        backgroundColor="mono3"
        backgroundPosition="50%"
        borderRadius={pxToRem(12)}
        backgroundRepeat="no-repeat"
        width={isMobile ? pxToRem(100) : pxToRem(126)}
        backgroundImage={`url("${accommodation.photo?.url || DEFAULT_HOTEL_IMAGE_WIDE}")`}
      />
      <Flex
        gap={pxToRem(8)}
        flexDirection="column"
        alignItems="flex-start"
      >
        <TextV2
          fontWeight={600}
          fontFamily={'Inter UI'}
          typographyVariant="hed2"
        >
          {accommodation.displayName}
        </TextV2>
        {accommodation.starRating && (
          <StarRating
            hideNonFilledStars
            showPartialRating={false}
            rating={accommodation.starRating}
            id="star-rating-hotel-suggestion-dialog"
          />
        )}
        <TextV2
          color={'mono10'}
          fontFamily={'Inter UI'}
          typographyVariant={'label1'}
        >
          <AddressMultiline address={addressObject} />
        </TextV2>
        {discountPercentage && (
          <Box
            border="1px solid"
            paddingY={pxToRem(6)}
            paddingX={pxToRem(12)}
            fontFamily={'Inter UI'}
            borderRadius={pxToRem(8)}
            borderColor={'newUIBrandPrimary'}
          >
            <TextV2
              fontFamily={'Inter UI'}
              color={'newUIBrandPrimary'}
              typographyVariant={'label2'}
            >
              {accTrans.linkInterceptor.savePercentage({ percentage: discountPercentage })}
            </TextV2>
          </Box>
        )}
      </Flex>
    </Flex>
  );
});

HotelInfoSection.displayName = 'HotelInfoSection';
